// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/ManageStaff/ConfirmationModal.js
import React, { useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';

const ConfirmationModal = ({ open, title, message, onConfirm, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);

  if (!open) return null;

  const handleConfirm = async () => {
    setIsLoading(true);
    await onConfirm(); // onConfirm should be async in parent
    setIsLoading(false);
    onCancel(); // Close modal after deletion
  };

  // Determine if the action is destructive (e.g., Delete or Revoke) to color-code gracefully
  const isDestructive = title?.toLowerCase().includes('delete') || 
                        title?.toLowerCase().includes('purge') || 
                        title?.toLowerCase().includes('revoke');

  return (
    <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 transition-opacity duration-200">
      
      {/* Dimmed Background Overlay */}
      <div 
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity" 
        onClick={isLoading ? undefined : onCancel}
      />

      {/* Main Bottom Sheet Drawer (Mobile) / Center Card Window (Desktop) */}
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-xl shadow-2xl flex flex-col max-h-[85vh] border border-slate-100 animate-slideUp sm:animate-scaleIn overflow-hidden">
        
        {/* Mobile Drag/Swipe Top Bar Indicator */}
        <div className="w-full flex justify-center py-2 sm:hidden shrink-0">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>

        {/* Modal Window Title Header */}
        <div className="px-4 pt-2 pb-3 sm:py-4 flex items-center justify-between border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className={isDestructive ? "text-rose-500" : "text-amber-500"} />
            <h2 className="text-sm font-bold text-slate-900">{title}</h2>
          </div>
          <button 
            type="button" 
            onClick={isLoading ? undefined : onCancel} 
            disabled={isLoading}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors disabled:opacity-30"
          >
            <X size={16} />
          </button>
        </div>

        {/* Message Content Context Frame Body */}
        <div className="p-4 space-y-4">
          <p className="text-xs font-medium text-slate-600 leading-relaxed">
            {message}
          </p>

          {/* Action Row Buttons Layout (Stacked full width on phones, grouped horizontally on desktop) */}
          <div className="flex flex-col-reverse sm:flex-row justify-end items-stretch sm:items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="h-10 sm:h-9 px-4 text-xs font-bold text-slate-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isLoading}
              className={`h-10 sm:h-9 px-5 text-xs font-bold text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 ${
                isLoading 
                  ? 'bg-slate-300 cursor-not-allowed' 
                  : isDestructive 
                    ? 'bg-rose-600 hover:bg-rose-700' 
                    : 'bg-slate-900 hover:bg-slate-800'
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 size={12} className="animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <span>Confirm</span>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ConfirmationModal;